import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { findMostRecentMetadata, getAbsoluteTimestamp, keySourceLookup, videoTimestampExtractor } from "components/util/PlaylistUtils";
import DtImageWithBbox from "./DtImageWithBbox";
import { updateSelectedSeenAnnotationsList } from "redux/actions/driverTrainingActions";
import { CheckOutlined, ClockCircleOutlined, StopOutlined } from "@ant-design/icons";
import { getCoordsAtTimestamp } from "components/util/GeometryUtilsV2";
import { routeGeomSelector } from "redux/selectors";

const userAnnotationsSelector = (state) => state.userAnnotations;
const playlistPositionSelector = (state) => state.playlist.position;
const playlistVideoDataSelector = (state) => state.playlist.data.video;
const selectedSessionSelector = (state) => state.driverTraining.trainee.selectedSession;
const currentPlayerIndexSelector = (state) => state.playlist.position.currentIndex;

// const timestampSelector = state => _.get(state.playlist, ["position", "timestamp"], null)
const timestampSelector = (state) => {
    const routeID = state.playlist.data.routeID;
    const isVideo = state.playlist.position.isVideo;
    const sourceIndex = state.playlist.position.sourceIndex;
    const playlist = isVideo ? _.get(state.playlist.data, ["video", sourceIndex], EMPTY_ARRAY) : state.playlist.data.image;
    const index = state.playlist.position.currentIndex;
    const offset = state.playlist.position.currentTimeOffset || 0;
    return getAbsoluteTimestamp(routeID, playlist, index, isVideo, offset);
};

const EMPTY_ARRAY = [];

const speedAndBearingSelector = (state) => _.get(state.routeMetadata, ["SPEED_AND_BEARING", state.playlist.data.routeID], EMPTY_ARRAY);
const tachoDataSelector = (state) => _.get(state.routeMetadata, ["TACHO_DATA", state.playlist.data.routeID], EMPTY_ARRAY);

const DtAnnotationComponent = () => {
    const dispatch = useDispatch();
    const userAnnotations = useSelector(userAnnotationsSelector);
    const playlistPosition = useSelector(playlistPositionSelector) || null;
    const playlistVideoData = useSelector(playlistVideoDataSelector);
    const selectedSession = useSelector(selectedSessionSelector);
    const routeGeometryData = useSelector(routeGeomSelector);

    const speedMetadata = useSelector(speedAndBearingSelector);
    const tachoMetadata = useSelector(tachoDataSelector);
    const timestamp = useSelector(timestampSelector);
    const [currentSpeed, setCurrentSpeed] = useState(0);

    const currentPlayerIndex = useSelector(currentPlayerIndexSelector);

    const selectedSessionSeenData = _.get(selectedSession, ["progress", "seen"], []);
    const selectedSessionProgressData = _.get(selectedSession, ["progress", "completed"], []);
    const selectedSessionSkippedData = _.get(selectedSession, ["progress", "skipped"], []);

    const previousValueRef = useRef(currentPlayerIndex);

    const [allUpcomingAnnotationsIDs, setAllUpcomingAnnotationsIDs] = useState([]);
    const [skippedAnnotationsIDs, setSkippedAnnotationsIDs] = useState([]);
    // const [adjustedDistance, setAdjustedDistance] = useState(0.05) // initially start with 50m distance, this will increase with train speed.

    useEffect(() => {
        const valueJumpThreshold = 1; // Set the threshold for value jump here

        // Calculate the difference between the current and previous value
        const valueDifference = Math.abs(currentPlayerIndex - previousValueRef.current);

        if (valueDifference > valueJumpThreshold) {
            // Call your function here to handle the jump
            handleVideoSkipped(currentPlayerIndex);
        }

        previousValueRef.current = currentPlayerIndex;
    }, [currentPlayerIndex]);

    // this handles when the video possition has been changed to detect if user trying to go through annotations without watching it.
    const handleVideoSkipped = (newValue) => {
        // if newValue is not 0 and nextAnnotation is set then mark as player has been skipped
        resetAnnotationsSeen();
        if (newValue) {
            setSkippedAnnotationsIDs(allUpcomingAnnotationsIDs);
        } else {
            setSkippedAnnotationsIDs([]);
        }
    };

    // this will calculate when to display annotation based on train speed and distance from current train position and the annotation location
    const adjustedTriggerDistance = useMemo(() => {
        let speed = _.get(findMostRecentMetadata(tachoMetadata, timestamp), ["data", "speed"]);
        if (speed == null) {
            speed = _.get(findMostRecentMetadata(speedMetadata, timestamp), ["data", "speed"], 0);
        }

        setCurrentSpeed(speed);
        const baseDistance = 0.1; // start with 100m from annotation
        const additionalDistancePerKmPerS = 0.03;
        return baseDistance + (speed - 1) * additionalDistancePerKmPerS;
    }, [speedMetadata, tachoMetadata, timestamp]);

    function calculateDistance(sourceLocation, targetLocation) {
        if (!targetLocation[0] && !targetLocation[1]) {
            return null;
        }
        const R = 6371; // Radius of the Earth in kilometers
        const dLat = ((targetLocation[0] - sourceLocation[0]) * Math.PI) / 180; // Difference in latitude
        const dLon = ((targetLocation[1] - sourceLocation[1]) * Math.PI) / 180; // Difference in longitude

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos((sourceLocation[0] * Math.PI) / 180) * Math.cos((targetLocation[0] * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        const distance = R * c; // Distance in kilometers
        return distance;
    }

    const sortedUserAnnotations = useMemo(() => {
        return _.sortBy(userAnnotations, "videoKey");
    }, [userAnnotations]);

    const annotationsTrackerList = useMemo(() => {
        let annotationTracker = [];
        if (sortedUserAnnotations && sortedUserAnnotations.length) {
            _.forEach(sortedUserAnnotations, (annotation) => {
                const keySource = keySourceLookup(annotation.videoKey, playlistVideoData);
                const playlistIndex = keySource[0];
                const sourceIndex = keySource[1];

                if (playlistIndex !== -1) {
                    const videoData = _.get(playlistVideoData, [sourceIndex], []);

                    const videoTimestamp = videoTimestampExtractor(_.get(videoData, [playlistIndex], null));
                    const coords = getCoordsAtTimestamp(videoTimestamp, routeGeometryData);

                    if (coords) {
                        annotationTracker.push({
                            ...annotation,
                            coords: [coords[0], coords[1]],
                        });
                    }
                }
            });
        }
        return annotationTracker;
    }, [sortedUserAnnotations, playlistVideoData, routeGeometryData]);

    const resetAnnotationsSeen = () => {
        console.log("debug4 resetting seen annotations... []");

        dispatch(updateSelectedSeenAnnotationsList(EMPTY_ARRAY));
    };

    const updateUpcomingAnnotationsIDs = useCallback(
        (newList) => {
            if (!_.isEqual(allUpcomingAnnotationsIDs, newList)) {
                setAllUpcomingAnnotationsIDs(newList);
            }
        },
        [allUpcomingAnnotationsIDs],
    );

    const upcomingAnnotations = useMemo(() => {
        if (playlistVideoData && playlistPosition && playlistPosition.coords && annotationsTrackerList.length) {
            let _nextAnnotation = null;
            let _allUpcomingAnnotationsIDs = [];
            let _upcomingAnnotations = [];
            let isFirst = true;

            _.forEach(annotationsTrackerList, (annotation, index) => {
                if (annotation) {
                    const distance = calculateDistance(annotation.coords, playlistPosition.coords);
                    const annotationSeen = _.includes(selectedSessionSeenData, annotation.id);

                    if (distance < adjustedTriggerDistance && !annotationSeen) {
                        _allUpcomingAnnotationsIDs.push(annotation.id);

                        if (isFirst) {
                            _nextAnnotation = (
                                <DtImageWithBbox
                                    key={annotation.id}
                                    annotation={annotation}
                                    first={index === 0}
                                    distance={distance}
                                    adjustedTriggerDistance={adjustedTriggerDistance}
                                    skippedAnnotationsIDs={skippedAnnotationsIDs}
                                    currentSpeed={currentSpeed}
                                />
                            );
                            isFirst = false;
                        } else {
                            _upcomingAnnotations.push(
                                <DtImageWithBbox
                                    key={annotation.id}
                                    annotation={annotation}
                                    first={index === 0}
                                    distance={distance}
                                    adjustedTriggerDistance={adjustedTriggerDistance}
                                    skippedAnnotationsIDs={skippedAnnotationsIDs}
                                    currentSpeed={currentSpeed}
                                />,
                            );
                        }
                    }
                }
            });
            updateUpcomingAnnotationsIDs(_allUpcomingAnnotationsIDs);

            return (
                <>
                    <div className="UpcomingAnnotationsGridItem UpcomingAnnotationsGridItemMain">{_nextAnnotation}</div>
                    <div className="UpcomingAnnotationsGridItem">{_upcomingAnnotations}</div>
                </>
            );
        }
    }, [
        adjustedTriggerDistance,
        annotationsTrackerList,
        currentSpeed,
        playlistPosition,
        playlistVideoData,
        selectedSessionSeenData,
        skippedAnnotationsIDs,
        updateUpcomingAnnotationsIDs,
    ]);

    const listOfSessionAnnotations = useMemo(() => {
        return _.map(annotationsTrackerList, (annotation) => {
            const isCompleted = _.includes(selectedSessionProgressData, String(annotation.id));
            const wasSkipped = _.includes(selectedSessionSkippedData, annotation.id);

            if (annotation) {
                return (
                    <div
                        className={`AnnotationListItem`}
                        key={annotation.id}>
                        <div className="AnnotationText">{annotation.custom_tag}</div>
                        <div className="AnnotationListItemBadge">
                            {!isCompleted ? (
                                wasSkipped ? (
                                    <StopOutlined style={{ fontSize: "16px", color: "#df4343" }} />
                                ) : (
                                    <ClockCircleOutlined style={{ fontSize: "16px", color: "#fa8c16" }} />
                                )
                            ) : (
                                <CheckOutlined style={{ fontSize: "16px", color: "#52c41a" }} />
                            )}
                        </div>
                    </div>
                );
            }
        });
    }, [annotationsTrackerList, selectedSessionProgressData, selectedSessionSkippedData]);

    useEffect(() => {
        console.log("debug4 annotationsTrackerList", annotationsTrackerList);
    }, [annotationsTrackerList]);

    return (
        <>
            <div className="AnnotationsContainer">
                <h4>Upcoming annotation:</h4>
                <div className="AnnotationsContainerMain">
                    <div className="UpcomingAnnotations">
                        <div className="UpcomingAnnotationsGrid">{upcomingAnnotations}</div>
                    </div>
                    <div className="ListOfAnnotations">{listOfSessionAnnotations}</div>
                </div>
            </div>
        </>
    );
};

export default DtAnnotationComponent;
